import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Download as DownloadIcon, File, Loader2, X as CloseIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toDisplayableOriginalFilename, type MessageAttachmentDto } from "@multi-chat/shared";
import { fetchMessageAttachmentContentBlob } from "../../../api/message-attachments-api.js";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import { downloadActionI18nKey, type AttachmentDownloadStatus } from "../attachment-download-state.js";
import {
  documentCardKind,
  documentExtension,
  documentKindAccentClass,
  formatHumanFileSize,
} from "../document-card.js";
import { documentPreviewMode } from "../document-preview.js";
import { shouldCloseImagePreviewOnBackdropPointerDown } from "../message-image-preview-logic.js";

export function MessageDocumentPreview({
  open,
  onOpenChange,
  organizationId,
  attachment,
  onDownload,
  downloadStatus,
  downloadDisabled,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  attachment: MessageAttachmentDto;
  onDownload: () => void;
  downloadStatus: AttachmentDownloadStatus;
  downloadDisabled: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const displayFileName = toDisplayableOriginalFilename(attachment.fileName);
  const mode = documentPreviewMode(displayFileName, attachment.mimeType);
  const kind = documentCardKind(displayFileName, attachment.mimeType);
  const fileName = displayFileName || t("inbox.messageType.DOCUMENT");
  const extension = documentExtension(displayFileName, attachment.mimeType);
  const sizeLabel = formatHumanFileSize(attachment.fileSizeBytes);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!open) {
      setObjectUrl(null);
      setTextContent(null);
      setLoadFailed(false);
      return;
    }

    let cancelled = false;
    let createdUrl: string | null = null;
    const controller = new AbortController();
    if (mode === "unavailable") {
      return;
    }
    void fetchMessageAttachmentContentBlob(
      organizationId,
      attachment.attachmentId,
      controller.signal,
    )
      .then(async (blob) => {
        const nextUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        createdUrl = nextUrl;
        setObjectUrl(nextUrl);
        if (mode === "text") {
          setTextContent(await blob.text());
        }
      })
      .catch((error: unknown) => {
        if (
          (error instanceof DOMException && error.name === "AbortError") ||
          (error instanceof Error && error.name === "AbortError")
        ) {
          return;
        }
        if (!cancelled) {
          setLoadFailed(true);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [attachment.attachmentId, mode, open, organizationId]);

  const meta = [extension, sizeLabel].filter(Boolean).join(" · ");

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[var(--z-alert-overlay)] bg-ink/85 animate-overlay-in"
          onPointerDown={(event) => {
            if (shouldCloseImagePreviewOnBackdropPointerDown(event.target, event.currentTarget)) {
              onOpenChange(false);
            }
          }}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-0 z-[var(--z-alert)] flex flex-col outline-none pointer-events-none",
            "data-[state=open]:animate-overlay-in",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            {t("inbox.document.previewTitle", { label: fileName })}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {t("inbox.document.previewDescription")}
          </DialogPrimitive.Description>

          <div className="pointer-events-auto flex items-center justify-end gap-2 px-3 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="border border-line/40 bg-ink/35 text-surface-elevated hover:bg-ink/50"
              disabled={downloadDisabled}
              onClick={onDownload}
            >
              {downloadStatus === "downloading" ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <DownloadIcon className="size-4" aria-hidden="true" />
              )}
              {t(downloadActionI18nKey(downloadStatus))}
            </Button>
            <DialogPrimitive.Close asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="border border-line/40 bg-ink/35 text-surface-elevated hover:bg-ink/50"
                aria-label={t("inbox.media.preview.close")}
              >
                <CloseIcon className="size-4" aria-hidden="true" />
              </Button>
            </DialogPrimitive.Close>
          </div>

          <div className="pointer-events-none flex min-h-0 flex-1 items-center justify-center px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {mode === "unavailable" ? (
              <div className="pointer-events-auto flex w-[min(22rem,92vw)] flex-col items-center gap-3 rounded-xl bg-surface px-6 py-8 text-center shadow-lg">
                <span
                  className={cn(
                    "flex size-14 items-center justify-center rounded-2xl",
                    documentKindAccentClass(kind),
                  )}
                >
                  <File className="size-7" aria-hidden="true" />
                </span>
                <p className="m-0 text-[0.9375rem] font-semibold text-ink">{fileName}</p>
                {meta ? <p className="m-0 text-[0.75rem] uppercase text-ink-subtle">{meta}</p> : null}
                <p className="m-0 text-[0.8125rem] text-ink-subtle">
                  {t("inbox.document.previewUnavailable")}
                </p>
              </div>
            ) : loadFailed ? (
              <p className="pointer-events-auto m-0 rounded-md bg-surface px-4 py-3 text-[0.8125rem] text-ink-subtle">
                {t("inbox.media.unavailable")}
              </p>
            ) : mode === "pdf" && objectUrl ? (
              <iframe
                title={fileName}
                src={objectUrl}
                className="pointer-events-auto h-[min(82dvh,100%)] w-[min(96vw,52rem)] rounded-md bg-surface"
              />
            ) : mode === "image" && objectUrl ? (
              <img
                src={objectUrl}
                alt={fileName}
                className="pointer-events-auto max-h-[min(82dvh,100%)] max-w-[min(96vw,100%)] object-contain"
              />
            ) : mode === "text" && textContent != null ? (
              <pre className="pointer-events-auto max-h-[min(82dvh,100%)] max-w-[min(96vw,52rem)] overflow-auto rounded-md bg-surface p-4 text-[0.8125rem] text-ink whitespace-pre-wrap">
                {textContent}
              </pre>
            ) : (
              <Loader2 className="size-8 animate-spin text-surface-elevated" aria-hidden="true" />
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
